/**
 * Página de Integrações do Sistema — cards por tipo + modal de configuração.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    PiCheckCircleBold,
    PiCheckCircleFill,
    PiGearBold,
    PiPlugBold,
    PiPlugsConnectedBold,
    PiWarningCircleBold,
    PiWifiHighBold,
    PiWifiSlashBold,
    PiXCircleBold,
    PiXCircleFill,
    PiEyeBold,
    PiEyeSlashBold,
    PiArrowsClockwiseBold,
} from 'react-icons/pi';
import { PageHeader, Skeleton } from '@v2/design-system';
import api from '@v2/lib/api';
import { cn } from '@v2/utils/cn';

/* ─── Types ─────────────────────────────────────────────────── */
type CredField = {
    key: string;
    label: string;
    type: 'text' | 'password' | 'url' | 'email';
    required: boolean;
};

type Integration = {
    type: string;
    label: string;
    color: string;
    id: number | null;
    name: string;
    is_active: boolean;
    is_configured: boolean;
    notes: string | null;
    updated_at: string | null;
    credential_fields?: CredField[];
    credentials?: Record<string, string | null>;
};

/* ─── Cores e ícones por tipo ──────────────────────────────── */
const TYPE_META: Record<string, {
    icon: React.ReactNode;
    description: string;
    accentFrom: string;
    accentTo: string;
    tag: string;
}> = {
    ploomes: {
        tag: 'CRM',
        description: 'Sincronização automática de deals, pedidos, contatos e webhooks em tempo real.',
        accentFrom: '#1d4ed8',
        accentTo: '#3b82f6',
        icon: (
            <svg viewBox="0 0 32 32" fill="none" className="size-7">
                <rect width="32" height="32" rx="8" fill="#1d4ed8" />
                <text x="16" y="22" textAnchor="middle" fontSize="14" fontWeight="900" fill="white">P</text>
            </svg>
        ),
    },
    protheus: {
        tag: 'ERP',
        description: 'Integração com ERP TOTVS Protheus para notas fiscais, estoque e financeiro.',
        accentFrom: '#059669',
        accentTo: '#10b981',
        icon: (
            <svg viewBox="0 0 32 32" fill="none" className="size-7">
                <rect width="32" height="32" rx="8" fill="#059669" />
                <text x="16" y="22" textAnchor="middle" fontSize="11" fontWeight="900" fill="white">ERP</text>
            </svg>
        ),
    },
    email: {
        tag: 'SMTP',
        description: 'Servidor SMTP para envio de e-mails transacionais, OTP e notificações.',
        accentFrom: '#7c3aed',
        accentTo: '#a78bfa',
        icon: (
            <svg viewBox="0 0 32 32" fill="none" className="size-7">
                <rect width="32" height="32" rx="8" fill="#7c3aed" />
                <path d="M6 10h20v12H6V10zm10 7L6 10h20l-10 7z" fill="white" fillOpacity=".9" />
            </svg>
        ),
    },
    wechat: {
        tag: 'MSG',
        description: 'WeChat Work (企业微信) para comunicação com fornecedores chineses.',
        accentFrom: '#16a34a',
        accentTo: '#4ade80',
        icon: (
            <svg viewBox="0 0 32 32" fill="none" className="size-7">
                <rect width="32" height="32" rx="8" fill="#16a34a" />
                <text x="16" y="22" textAnchor="middle" fontSize="9" fontWeight="900" fill="white">WeChat</text>
            </svg>
        ),
    },
};

/* ─── Fetch ─────────────────────────────────────────────────── */
function useIntegrations() {
    return useQuery<Integration[]>({
        queryKey: ['system-integrations'],
        queryFn: async () => {
            const { data } = await api.get('/system-integrations');
            return data.data as Integration[];
        },
    });
}

function useIntegrationDetail(type: string | null) {
    return useQuery<Integration>({
        queryKey: ['system-integrations', type],
        queryFn: async () => {
            const { data } = await api.get(`/system-integrations/${type}`);
            return data as Integration;
        },
        enabled: !!type,
        staleTime: 0,
    });
}

/* ─── Utilitário de formatação de data ──────────────────────── */
function fmtDate(iso: string | null | undefined) {
    if (!iso) return null;
    return new Date(iso).toLocaleDateString('pt-BR', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });
}

/* ─── Badge de status ───────────────────────────────────────── */
function StatusBadge({ active, configured }: { active: boolean; configured: boolean }) {
    if (!configured) {
        return (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-[11px] font-black uppercase tracking-widest text-amber-700 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-400">
                <span className="size-1.5 rounded-full bg-amber-500 dark:bg-amber-400" />
                Não configurada
            </span>
        );
    }
    if (active) {
        return (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-0.5 text-[11px] font-black uppercase tracking-widest text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-400">
                <span className="size-1.5 animate-pulse rounded-full bg-emerald-500 dark:bg-emerald-400" />
                Ativa
            </span>
        );
    }
    return (
        <span className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-gray-50 px-2.5 py-0.5 text-[11px] font-black uppercase tracking-widest text-gray-500 dark:border-zinc-600/40 dark:bg-zinc-800/40 dark:text-zinc-400">
            <span className="size-1.5 rounded-full bg-gray-400 dark:bg-zinc-600" />
            Inativa
        </span>
    );
}

/* ─── Card de integração ─────────────────────────────────────── */
function IntegrationCard({
    integration,
    onConfigure,
}: {
    integration: Integration;
    onConfigure: (type: string) => void;
}) {
    const meta = TYPE_META[integration.type] ?? {
        tag: integration.type.toUpperCase(),
        description: '',
        accentFrom: '#C9A84C',
        accentTo: '#F0D06A',
        icon: <PiPlugBold className="size-7 text-gray-700 dark:text-white" />,
    };

    return (
        <div className={cn(
            'group relative flex flex-col overflow-hidden rounded-2xl border transition-all duration-200',
            'border-gray-200 bg-white hover:border-gray-300 hover:shadow-md',
            'dark:border-zinc-800 dark:bg-zinc-900 dark:hover:border-zinc-700 dark:hover:shadow-xl',
        )}>
            <div
                className="h-1 w-full shrink-0"
                style={{ background: `linear-gradient(90deg, ${meta.accentFrom}, ${meta.accentTo})` }}
            />

            <div className="flex flex-1 flex-col p-6">
                <div className="mb-4 flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3">
                        <div className="shrink-0">{meta.icon}</div>
                        <div>
                            <div className="flex flex-wrap items-center gap-2">
                                <h3 className="text-lg font-black text-gray-900 dark:text-white">{integration.name}</h3>
                                <span
                                    className="rounded-md px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest"
                                    style={{
                                        background: `${meta.accentFrom}22`,
                                        color: meta.accentFrom,
                                        border: `1px solid ${meta.accentFrom}44`,
                                    }}
                                >
                                    {meta.tag}
                                </span>
                            </div>
                            <p className="mt-0.5 font-mono text-xs text-gray-400 dark:text-zinc-500">{integration.type}</p>
                        </div>
                    </div>

                    <StatusBadge active={integration.is_active} configured={integration.is_configured} />
                </div>

                <p className="mb-5 text-sm leading-relaxed text-gray-500 dark:text-zinc-400">{meta.description}</p>

                <div className="mb-5 flex flex-wrap gap-3">
                    <div className={cn(
                        'flex items-center gap-2 rounded-xl border px-3 py-2 text-xs font-bold',
                        integration.is_configured
                            ? 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/8 dark:text-emerald-400'
                            : 'border-gray-200 bg-gray-50 text-gray-400 dark:border-zinc-700/40 dark:bg-zinc-800/40 dark:text-zinc-500',
                    )}>
                        {integration.is_configured
                            ? <PiCheckCircleFill className="size-3.5" />
                            : <PiXCircleFill className="size-3.5" />
                        }
                        Credenciais
                    </div>
                    <div className={cn(
                        'flex items-center gap-2 rounded-xl border px-3 py-2 text-xs font-bold',
                        integration.is_active
                            ? 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/8 dark:text-emerald-400'
                            : 'border-gray-200 bg-gray-50 text-gray-400 dark:border-zinc-700/40 dark:bg-zinc-800/40 dark:text-zinc-500',
                    )}>
                        {integration.is_active
                            ? <PiPlugsConnectedBold className="size-3.5" />
                            : <PiPlugBold className="size-3.5" />
                        }
                        {integration.is_active ? 'Habilitada' : 'Desabilitada'}
                    </div>
                </div>

                {integration.updated_at && (
                    <p className="mb-5 text-[11px] text-gray-400 dark:text-zinc-600">
                        Atualizado em {fmtDate(integration.updated_at)}
                    </p>
                )}

                {integration.notes && (
                    <p className="mb-4 rounded-xl border border-gray-100 bg-gray-50 p-3 text-xs italic leading-relaxed text-gray-500 dark:border-zinc-800 dark:bg-zinc-800/50 dark:text-zinc-400">
                        {integration.notes}
                    </p>
                )}

                <div className="mt-auto">
                    <button
                        type="button"
                        onClick={() => onConfigure(integration.type)}
                        className={cn(
                            'flex w-full items-center justify-center gap-2 rounded-xl border px-4 py-2.5 text-sm font-bold transition',
                            'border-gray-200 bg-gray-50 text-gray-800 hover:border-gray-300 hover:bg-gray-100',
                            'dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-200 dark:hover:border-zinc-600 dark:hover:bg-zinc-700',
                        )}
                    >
                        <PiGearBold className="size-4" />
                        Configurar
                    </button>
                </div>
            </div>
        </div>
    );
}

/* ─── Campo de credencial ───────────────────────────────────── */
function CredentialField({
    field,
    value,
    onChange,
}: {
    field: CredField;
    value: string;
    onChange: (key: string, val: string) => void;
}) {
    const [show, setShow] = useState(false);
    const isPassword = field.type === 'password';
    const inputType = isPassword && !show ? 'password' : 'text';

    return (
        <div>
            <label className="mb-1.5 block text-xs font-bold text-gray-600 dark:text-zinc-400">
                {field.label}
                {field.required && <span className="ml-1 text-red-500 dark:text-red-400">*</span>}
            </label>
            <div className="relative">
                <input
                    type={inputType}
                    value={value}
                    onChange={(e) => onChange(field.key, e.target.value)}
                    placeholder={isPassword && value === '••••••' ? 'Deixe em branco para manter atual' : ''}
                    className={cn(
                        'w-full rounded-xl border px-3 py-2.5 text-sm outline-none transition',
                        'border-gray-200 bg-white text-gray-900 placeholder:text-gray-400',
                        'focus:border-primary focus:ring-1 focus:ring-primary/30',
                        'dark:border-zinc-700 dark:bg-zinc-800 dark:text-white dark:placeholder:text-zinc-600',
                        'dark:focus:border-[#C9A84C] dark:focus:ring-[#C9A84C]/30',
                        isPassword && 'pr-10',
                    )}
                    onFocus={(e) => {
                        if (e.target.value === '••••••') {
                            onChange(field.key, '');
                        }
                    }}
                />
                {isPassword && (
                    <button
                        type="button"
                        onClick={() => setShow((s) => !s)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:text-zinc-500 dark:hover:text-zinc-300"
                    >
                        {show ? <PiEyeSlashBold className="size-4" /> : <PiEyeBold className="size-4" />}
                    </button>
                )}
            </div>
        </div>
    );
}

/* ─── Modal de configuração ─────────────────────────────────── */
function ConfigModal({
    type,
    onClose,
}: {
    type: string;
    onClose: () => void;
}) {
    const qc = useQueryClient();
    const { data: detail, isLoading } = useIntegrationDetail(type);
    const meta = TYPE_META[type];

    const [form, setForm] = useState<Record<string, string>>({});
    const [isActive, setIsActive] = useState(false);
    const [notes, setNotes] = useState('');
    const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
    const [isTesting, setIsTesting] = useState(false);

    useEffect(() => {
        if (detail) {
            setIsActive(detail.is_active);
            setNotes(detail.notes ?? '');
            const initial: Record<string, string> = {};
            (detail.credential_fields ?? []).forEach((f) => {
                initial[f.key] = (detail.credentials?.[f.key] as string) ?? '';
            });
            setForm(initial);
        }
    }, [detail]);

    const saveMutation = useMutation({
        mutationFn: async () => {
            await api.put(`/system-integrations/${type}`, {
                is_active: isActive,
                notes: notes || null,
                credentials: form,
            });
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['system-integrations'] });
            onClose();
        },
    });

    const handleTest = async () => {
        setIsTesting(true);
        setTestResult(null);
        try {
            const { data } = await api.post(`/system-integrations/${type}/test`);
            setTestResult(data);
        } catch (err: unknown) {
            const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Erro ao testar conexão.';
            setTestResult({ success: false, message: msg });
        } finally {
            setIsTesting(false);
        }
    };

    const backdropRef = useRef<HTMLDivElement>(null);
    const handleBackdrop = useCallback((e: React.MouseEvent) => {
        if (e.target === backdropRef.current) onClose();
    }, [onClose]);

    return (
        <div
            ref={backdropRef}
            onClick={handleBackdrop}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm dark:bg-black/70"
        >
            <div className="flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl dark:border-zinc-800 dark:bg-zinc-950">

                <div className="flex shrink-0 items-center justify-between border-b border-gray-100 p-5 dark:border-zinc-800">
                    <div className="flex items-center gap-3">
                        <div className="shrink-0">{meta?.icon ?? <PiPlugBold className="size-7 text-gray-700 dark:text-white" />}</div>
                        <div>
                            <h2 className="text-lg font-black text-gray-900 dark:text-white">
                                {detail?.name ?? type}
                            </h2>
                            <p className="text-xs text-gray-400 dark:text-zinc-500">{meta?.tag} · Configuração</p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-xl p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:text-zinc-500 dark:hover:bg-zinc-800 dark:hover:text-zinc-300"
                    >
                        <PiXCircleBold className="size-5" />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-5">
                    {isLoading ? (
                        <div className="space-y-4 py-2">
                            <Skeleton className="h-16 w-full rounded-xl" />
                            <Skeleton className="h-40 w-full rounded-xl" />
                            <Skeleton className="h-24 w-full rounded-xl" />
                        </div>
                    ) : (
                        <div className="flex flex-col gap-5">

                            <div className="flex items-center justify-between rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-zinc-800 dark:bg-zinc-900">
                                <div>
                                    <p className="text-sm font-bold text-gray-900 dark:text-white">Integração ativa</p>
                                    <p className="text-xs text-gray-500 dark:text-zinc-500">Habilita o uso desta integração no sistema</p>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setIsActive((v) => !v)}
                                    className={cn(
                                        'relative shrink-0 rounded-full transition-colors duration-200',
                                        isActive ? 'bg-emerald-500' : 'bg-gray-300 dark:bg-zinc-700',
                                    )}
                                    style={{ width: 44, height: 24 }}
                                >
                                    <span
                                        className="absolute top-[2px] size-5 rounded-full bg-white shadow-md transition-all duration-200"
                                        style={{ left: isActive ? 22 : 2 }}
                                    />
                                </button>
                            </div>

                            {(detail?.credential_fields ?? []).length > 0 && (
                                <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-zinc-800 dark:bg-zinc-900">
                                    <p className="mb-4 text-xs font-black uppercase tracking-widest text-gray-400 dark:text-zinc-500">
                                        Credenciais
                                    </p>
                                    <div className="flex flex-col gap-3">
                                        {(detail?.credential_fields ?? []).map((field) => (
                                            <CredentialField
                                                key={field.key}
                                                field={field}
                                                value={form[field.key] ?? ''}
                                                onChange={(key, val) => setForm((prev) => ({ ...prev, [key]: val }))}
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div>
                                <label className="mb-1.5 block text-xs font-bold text-gray-600 dark:text-zinc-400">
                                    Observações (opcional)
                                </label>
                                <textarea
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    rows={3}
                                    className={cn(
                                        'w-full resize-none rounded-xl border px-3 py-2.5 text-sm outline-none transition',
                                        'border-gray-200 bg-white text-gray-900 placeholder:text-gray-400',
                                        'focus:border-primary focus:ring-1 focus:ring-primary/30',
                                        'dark:border-zinc-700 dark:bg-zinc-800 dark:text-white dark:placeholder:text-zinc-600',
                                        'dark:focus:border-[#C9A84C] dark:focus:ring-[#C9A84C]/30',
                                    )}
                                    placeholder="Ambiente, versão, notas da equipe…"
                                />
                            </div>

                            {testResult && (
                                <div className={cn(
                                    'flex items-start gap-3 rounded-xl border p-4',
                                    testResult.success
                                        ? 'border-emerald-200 bg-emerald-50 dark:border-emerald-500/30 dark:bg-emerald-500/8'
                                        : 'border-red-200 bg-red-50 dark:border-red-500/30 dark:bg-red-500/8',
                                )}>
                                    {testResult.success
                                        ? <PiCheckCircleBold className="mt-0.5 size-5 shrink-0 text-emerald-600 dark:text-emerald-400" />
                                        : <PiWarningCircleBold className="mt-0.5 size-5 shrink-0 text-red-600 dark:text-red-400" />
                                    }
                                    <p className={cn(
                                        'text-sm leading-relaxed',
                                        testResult.success
                                            ? 'text-emerald-800 dark:text-emerald-300'
                                            : 'text-red-800 dark:text-red-300',
                                    )}>
                                        {testResult.message}
                                    </p>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className="shrink-0 border-t border-gray-100 p-5 dark:border-zinc-800">
                    <div className="flex flex-wrap items-center gap-3">
                        <button
                            type="button"
                            onClick={handleTest}
                            disabled={isTesting || !detail?.is_configured}
                            className={cn(
                                'flex items-center gap-2 rounded-xl border px-4 py-2.5 text-sm font-bold transition disabled:cursor-not-allowed disabled:opacity-40',
                                'border-gray-200 bg-gray-50 text-gray-700 hover:bg-gray-100',
                                'dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-300 dark:hover:bg-zinc-700',
                            )}
                            title={!detail?.is_configured ? 'Configure as credenciais primeiro' : ''}
                        >
                            {isTesting
                                ? <PiArrowsClockwiseBold className="size-4 animate-spin" />
                                : (testResult?.success
                                    ? <PiWifiHighBold className="size-4 text-emerald-500 dark:text-emerald-400" />
                                    : testResult
                                        ? <PiWifiSlashBold className="size-4 text-red-500 dark:text-red-400" />
                                        : <PiWifiHighBold className="size-4" />
                                )
                            }
                            Testar conexão
                        </button>

                        <div className="flex-1" />

                        <button
                            type="button"
                            onClick={onClose}
                            className={cn(
                                'rounded-xl border px-4 py-2.5 text-sm font-bold transition',
                                'border-gray-200 bg-gray-50 text-gray-700 hover:bg-gray-100',
                                'dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-300 dark:hover:bg-zinc-700',
                            )}
                        >
                            Cancelar
                        </button>

                        <button
                            type="button"
                            onClick={() => saveMutation.mutate()}
                            disabled={saveMutation.isPending}
                            className="flex items-center gap-2 rounded-xl px-5 py-2.5 text-sm font-black transition disabled:opacity-60"
                            style={{
                                background: 'linear-gradient(135deg, #8B6914 0%, #C9A84C 50%, #F0D06A 100%)',
                                color: '#1a1200',
                                boxShadow: '0 4px 16px rgba(201,168,76,0.25)',
                            }}
                        >
                            {saveMutation.isPending
                                ? <PiArrowsClockwiseBold className="size-4 animate-spin" />
                                : <PiCheckCircleBold className="size-4" />
                            }
                            Salvar
                        </button>
                    </div>

                    {saveMutation.isError && (
                        <p className="mt-3 text-xs text-red-600 dark:text-red-400">
                            Erro ao salvar. Verifique os campos e tente novamente.
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
}

/* ─── PÁGINA PRINCIPAL ──────────────────────────────────────── */
export default function SystemIntegrationsPage() {
    const { data: integrations, isLoading } = useIntegrations();
    const [configuringType, setConfiguringType] = useState<string | null>(null);

    const activeCount = integrations?.filter((i) => i.is_active).length ?? 0;
    const configuredCount = integrations?.filter((i) => i.is_configured).length ?? 0;
    const totalCount = integrations?.length ?? 0;

    return (
        <div className="space-y-6">
            <PageHeader
                title="Integrações"
                breadcrumb={[
                    { name: 'Configurações', href: '/settings/vessels' },
                    { name: 'Integrações' },
                ]}
            >
                {!isLoading && (
                    <div className="flex items-center gap-3">
                        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2 text-center dark:border-emerald-500/20 dark:bg-emerald-500/8">
                            <p className="text-xl font-black text-emerald-700 dark:text-emerald-400">{activeCount}</p>
                            <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-600/80 dark:text-emerald-600">Ativas</p>
                        </div>
                        <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-2 text-center dark:border-zinc-700/50 dark:bg-zinc-800/50">
                            <p className="text-xl font-black text-gray-900 dark:text-white">{configuredCount}/{totalCount}</p>
                            <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 dark:text-zinc-500">Configuradas</p>
                        </div>
                    </div>
                )}
            </PageHeader>

            <p className="text-sm text-gray-500 dark:text-zinc-500">
                Configure as conexões com sistemas externos utilizados na operação.
            </p>

            {!isLoading && totalCount > 0 && (
                <div>
                    <div className="mb-1 flex items-center justify-between text-[11px] text-gray-400 dark:text-zinc-500">
                        <span>{configuredCount} de {totalCount} integrações configuradas</span>
                        <span>{Math.round((configuredCount / totalCount) * 100)}%</span>
                    </div>
                    <div className="h-1.5 overflow-hidden rounded-full bg-gray-100 dark:bg-zinc-800">
                        <div
                            className="h-full rounded-full transition-all duration-700"
                            style={{
                                width: `${(configuredCount / totalCount) * 100}%`,
                                background: 'linear-gradient(90deg, #8B6914, #C9A84C, #F0D06A)',
                            }}
                        />
                    </div>
                </div>
            )}

            {isLoading ? (
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                    {[1, 2, 3, 4].map((i) => (
                        <div key={i} className="space-y-3 rounded-2xl border border-gray-200 p-6 dark:border-zinc-800">
                            <div className="flex items-center gap-3">
                                <Skeleton className="size-8 rounded-lg" />
                                <div className="flex-1 space-y-2">
                                    <Skeleton className="h-5 w-32" />
                                    <Skeleton className="h-3 w-16" />
                                </div>
                            </div>
                            <Skeleton className="h-12 w-full" />
                            <Skeleton className="h-10 w-full rounded-xl" />
                        </div>
                    ))}
                </div>
            ) : (
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                    {(integrations ?? []).map((integration) => (
                        <IntegrationCard
                            key={integration.type}
                            integration={integration}
                            onConfigure={setConfiguringType}
                        />
                    ))}
                </div>
            )}

            <div className="rounded-2xl border border-amber-200/60 bg-amber-50/50 p-4 dark:border-[#C9A84C]/15 dark:bg-[#C9A84C]/5">
                <div className="flex items-start gap-3">
                    <span className="mt-0.5 text-lg" aria-hidden>🔒</span>
                    <div>
                        <p className="text-sm font-bold text-amber-800 dark:text-[#C9A84C]">Segurança das credenciais</p>
                        <p className="mt-0.5 text-xs leading-relaxed text-amber-900/70 dark:text-zinc-500">
                            Todas as credenciais são armazenadas criptografadas no banco de dados.
                            Campos de senha nunca são exibidos após salvos — apenas substituídos quando um novo valor é fornecido.
                        </p>
                    </div>
                </div>
            </div>

            {configuringType && (
                <ConfigModal
                    type={configuringType}
                    onClose={() => setConfiguringType(null)}
                />
            )}
        </div>
    );
}
